import { useState } from "react";
import { JobCardForm, DATA_ADDONS, DELIVERABLE_OPTIONS, STATUS_OPTIONS, StatusEntry } from "@/pages/jobcards/types";

interface Props {
  form: JobCardForm;
  onChange: (patch: Partial<JobCardForm>) => void;
}

const statusColors: Record<string, { bg: string; text: string; border: string }> = {
  Pending:     { bg: "#fffbeb", text: "#d97706", border: "#fcd34d" },
  "In Progress": { bg: "#eff6ff", text: "#2563eb", border: "#93c5fd" },
  Completed:   { bg: "#f0fdf4", text: "#16a34a", border: "#86efac" },
  Delivered:   { bg: "#f5f3ff", text: "#7c3aed", border: "#c4b5fd" },
};

export default function SectionDeliveryStatus({ form, onChange }: Props) {
  const [newStatusNote, setNewStatusNote] = useState("");
  const [newStatusUser, setNewStatusUser] = useState("Rajesh Kumar");

  const toggleAddon = (addon: string) => {
    const cur = form.dataAddons;
    onChange({ dataAddons: cur.includes(addon) ? cur.filter((a) => a !== addon) : [...cur, addon] });
  };

  const toggleDeliverable = (d: string) => {
    const cur = form.deliverables;
    onChange({ deliverables: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d] });
  };

  const addStatusEntry = () => {
    if (!newStatusNote.trim()) return;
    const now = new Date();
    const entry: StatusEntry = {
      status: form.currentStatus,
      date: now.toISOString().split("T")[0],
      time: now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
      user: newStatusUser,
      description: newStatusNote.trim(),
    };
    onChange({ statusTimeline: [...form.statusTimeline, entry] });
    setNewStatusNote("");
  };

  const sc = statusColors[form.currentStatus] || statusColors["Pending"];

  return (
    <>
      {/* Section 10 — Completion Date */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-calendar-todo-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 10 — Work Completion Estimation</h3>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Expected Completion Date <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-calendar-event-line text-gray-400 text-sm" />
              </span>
              <input
                type="date"
                value={form.completionDate}
                onChange={(e) => onChange({ completionDate: e.target.value })}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Site Conditions / Remarks</label>
            <input
              type="text"
              value={form.siteConditions}
              onChange={(e) => onChange({ siteConditions: e.target.value })}
              placeholder="e.g. Rocky terrain, access road required"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Section 12 — Data Processing Addons */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-cpu-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 12 — Data Processing Addons</h3>
          {form.dataAddons.length > 0 && (
            <span className="ml-auto text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
              {form.dataAddons.length} selected
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {DATA_ADDONS.map((addon) => {
              const selected = form.dataAddons.includes(addon);
              return (
                <button
                  key={addon}
                  type="button"
                  onClick={() => toggleAddon(addon)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-all cursor-pointer text-left ${
                    selected
                      ? "border-amber-400 bg-amber-50 text-amber-700"
                      : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200"
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 border ${selected ? "bg-amber-500 border-amber-500" : "border-gray-300"}`}>
                    {selected && <i className="ri-check-line text-white text-xs" />}
                  </span>
                  {addon}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 13 — Status Timeline */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-time-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 13 — Status Timeline</h3>
        </div>
        <div className="p-5">
          {/* Current Status Selector */}
          <div className="mb-5">
            <label className="block text-xs font-semibold text-gray-600 mb-2">Current Job Status</label>
            <div className="flex items-center gap-2 flex-wrap">
              {STATUS_OPTIONS.map((s) => {
                const c = statusColors[s];
                const active = form.currentStatus === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onChange({ currentStatus: s })}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all cursor-pointer whitespace-nowrap ${
                      active
                        ? ""
                        : "border-gray-200 text-gray-500 bg-white hover:border-gray-300"
                    }`}
                    style={active ? { background: c.bg, color: c.text, borderColor: c.border } : {}}
                  >
                    {active && <i className="ri-checkbox-circle-fill mr-1.5" />}
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Add Status Note */}
          <div className="mb-5 p-4 rounded-xl border border-dashed border-gray-200 bg-gray-50">
            <p className="text-xs font-semibold text-gray-600 mb-3">Add Status Update Note</p>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newStatusUser}
                onChange={(e) => setNewStatusUser(e.target.value)}
                placeholder="Updated by"
                className="w-40 px-3 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
              <input
                type="text"
                value={newStatusNote}
                onChange={(e) => setNewStatusNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addStatusEntry()}
                placeholder="Describe the status update..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
              <button
                type="button"
                onClick={addStatusEntry}
                className="px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-all"
              >
                Add
              </button>
            </div>
            <p className="text-xs text-gray-400">Press Enter or click Add to log the update</p>
          </div>

          {/* Timeline */}
          {form.statusTimeline.length === 0 ? (
            <div className="text-center py-6 text-gray-400">
              <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                <i className="ri-time-line text-3xl text-gray-200" />
              </span>
              <p className="text-xs">No status updates yet. Add the first update above.</p>
            </div>
          ) : (
            <div className="space-y-0">
              {form.statusTimeline.map((entry, i) => {
                const c = statusColors[entry.status] || statusColors["Pending"];
                return (
                  <div key={i} className="flex gap-3 relative">
                    <div className="flex flex-col items-center">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2"
                        style={{ background: c.bg, borderColor: c.border }}
                      >
                        <i className="ri-checkbox-circle-line text-sm" style={{ color: c.text }} />
                      </div>
                      {i < form.statusTimeline.length - 1 && (
                        <div className="w-px flex-1 bg-gray-100 my-1" style={{ minHeight: "16px" }} />
                      )}
                    </div>
                    <div className="pb-4 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: c.bg, color: c.text }}>
                          {entry.status}
                        </span>
                        <span className="text-xs text-gray-400">{entry.date} at {entry.time}</span>
                        <span className="text-xs text-gray-500 font-medium">by {entry.user}</span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{entry.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onChange({ statusTimeline: form.statusTimeline.filter((_, idx) => idx !== i) })}
                      className="w-6 h-6 flex items-center justify-center rounded text-gray-300 hover:text-red-400 cursor-pointer flex-shrink-0 mt-1"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Section 14 — Deliverables */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-inbox-archive-line text-amber-600 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-gray-800">Section 14 — Deliverables</h3>
          {form.deliverables.length > 0 && (
            <span className="ml-auto text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
              {form.deliverables.length} selected
            </span>
          )}
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            {DELIVERABLE_OPTIONS.map((d) => {
              const selected = form.deliverables.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDeliverable(d)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all cursor-pointer text-left ${
                    selected
                      ? "border-amber-400 bg-amber-50 text-amber-700"
                      : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200"
                  }`}
                >
                  <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${selected ? "bg-amber-500 border-amber-500" : "border-gray-300"}`}>
                    {selected && <i className="ri-check-line text-white text-xs" />}
                  </span>
                  {d}
                </button>
              );
            })}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Custom Deliverable</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={form.customDeliverable}
                onChange={(e) => onChange({ customDeliverable: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && form.customDeliverable.trim()) {
                    onChange({
                      deliverables: [...form.deliverables, form.customDeliverable.trim()],
                      customDeliverable: "",
                    });
                  }
                }}
                placeholder="Type custom deliverable and press Enter..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
              <button
                type="button"
                onClick={() => {
                  if (form.customDeliverable.trim()) {
                    onChange({
                      deliverables: [...form.deliverables, form.customDeliverable.trim()],
                      customDeliverable: "",
                    });
                  }
                }}
                className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap transition-all"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
